import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Product3DViewer } from "@/components/Product3DViewer";

const SUPPORT_POINTS = [
  "Mess-free application",
  "Fragrance free",
  "Dermatologically gentle",
  "Suitable for sensitive skin",
];

export const Hero = () => {
  const navigate = useNavigate();
  const goHash = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative overflow-hidden bg-[#F8F5EF]" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start lg:items-center">
          {/* Headline (top) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="order-1 lg:order-none lg:col-start-1 lg:row-start-1 text-center lg:text-left"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#B8C4A3]/25 text-[#6F503A] text-sm font-medium mb-6">
              Gentle by nature · Trusted by parents
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#6F503A] leading-[1.08]">
              Gentle Skin Relief For Babies
            </h1>
          </motion.div>

          {/* 3D balm — on mobile sits right below the headline */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center relative"
          >
            {/* Heavenly halo: soft warm core glow + light rays peeking through */}
            <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
              <div className="hero-rays w-[520px] h-[520px] md:w-[640px] md:h-[640px] rounded-full opacity-80" />
            </div>
            <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
              <div className="hero-halo w-[300px] h-[300px] md:w-[380px] md:h-[380px] rounded-full" />
            </div>
            <Product3DViewer
              src="/assets/balm.glb"
              fallbackImage="/assets/balm.png"
              heightClass="h-[360px] md:h-[460px]"
              testId="hero-balm-3d"
              sparkles
            />
            <p className="text-center text-xs text-[#6F503A]/40 mt-1">Drag to spin · Baby Hemp Calm Balm Stick</p>
          </motion.div>

          {/* Rest of the text (subheading, CTAs, points) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 text-center lg:text-left"
          >
            <p className="text-lg text-[#6F503A]/70 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              A mess-free balm stick designed to comfort dry, sensitive and eczema-prone skin with a simple daily routine.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center lg:justify-start">
              <button
                data-testid="hero-shop-balm-btn"
                onClick={() => navigate("/product/balm")}
                className="px-7 py-4 rounded-full bg-[#6F503A] text-[#F8F5EF] font-medium hover:bg-[#5b4130] transition-colors"
              >
                Shop Calm Balm
              </button>
              <button
                data-testid="hero-build-bundle-btn"
                onClick={() => goHash("bundle-builder")}
                className="px-7 py-4 rounded-full border border-[#6F503A]/25 text-[#6F503A] font-medium hover:bg-[#E9E2D6] transition-colors"
              >
                Build Your Bundle
              </button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 justify-center lg:justify-start">
              {SUPPORT_POINTS.map((p) => (
                <span key={p} className="inline-flex items-center gap-2 text-sm text-[#6F503A]/80">
                  <span className="w-5 h-5 rounded-full bg-[#B8C4A3]/25 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#6F503A]" />
                  </span>
                  {p}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
